import { createFileRoute } from '@tanstack/react-router';

import { LinkPage } from '../auth/link-page';
import { useAppNavigate } from '../lib/session/navigate';
import { DEFAULT_NEXT_PATH, safeNextPath } from '../lib/session/next-path';

/**
 * `/auth/link?token=…&next=…` — the landing page of an emailed sign-in link.
 *
 * `next` is validated twice: once here, and once inside `LinkPage` before it navigates.
 * That is deliberate duplication — this page creates a session, and an open redirect on
 * the one screen that does so is the most valuable one to an attacker.
 */

interface LinkSearch {
  token?: string;
  next?: string;
}

export const Route = createFileRoute('/auth/link')({
  validateSearch: (search: Record<string, unknown>): LinkSearch => ({
    ...(typeof search['token'] === 'string' && search['token'] !== ''
      ? { token: search['token'] }
      : {}),
    ...(typeof search['next'] === 'string'
      ? { next: safeNextPath(search['next'], DEFAULT_NEXT_PATH) }
      : {}),
  }),
  component: AuthLinkRoute,
});

function AuthLinkRoute() {
  const { token, next } = Route.useSearch();
  const navigate = useAppNavigate();

  return (
    <LinkPage
      {...(token === undefined ? {} : { token })}
      {...(next === undefined ? {} : { next })}
      onSignedIn={(path) => {
        navigate(path, { replace: true });
      }}
      onNavigate={navigate}
    />
  );
}
